// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { MathUtils } from '@shared/utils/MathUtils';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderExecutionType } from '@shared/utils/Trading/OrderExecutionType';
import { OrderTifMap } from '@shared/utils/Trading/OrderTifEnum';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { Resources } from '@shared/localizations/Resources';
import { DataCache } from '../../DataCache';
import { BoughtSoldUtils } from '@shared/utils/Instruments/BoughtSoldUtils';
import { BaseSettings } from '../../Settings/BaseGeneralSettingsWrapper';
class _HistoryOrderFormatter {
    IdStr(historyOrder) {
        return historyOrder.OrderNumber;
    }
    IsLong(historyOrder) {
        return historyOrder.BuySell === OperationType.Buy;
    }
    Quantity(historyOrder) {
        if (BaseSettings.isQuantityInLots) {
            return historyOrder.Quantity;
        }
        else {
            const instrument = historyOrder.Instrument;
            if (instrument !== null && instrument !== undefined) {
                return historyOrder.Quantity * instrument.LotSize;
            }
            else {
                return historyOrder.Quantity;
            }
        }
    }
    QuantityStr(historyOrder) {
        const amount = this.Quantity(historyOrder);
        let amountStr = amount.toString();
        if (historyOrder.Instrument !== null) {
            amountStr = DataCache.formatVolume(historyOrder.Instrument, Math.abs(amount), BaseSettings.isQuantityInLots, historyOrder.ProductType, historyOrder.Account);
        }
        return amountStr;
    }
    AccountStr(historyOrder) {
        var _a;
        const account = historyOrder.Account;
        if (account !== null && account !== undefined) {
            return account.toString();
        }
        else {
            return (_a = historyOrder.AccountStr) !== null && _a !== void 0 ? _a : '';
        }
    }
    SymbolStr(historyOrder) {
        var _a;
        const instrument = historyOrder.Instrument;
        if (instrument !== null && instrument !== undefined) {
            return instrument.DisplayShortName();
        }
        else {
            return (_a = historyOrder.InstrumentStr) !== null && _a !== void 0 ? _a : '';
        }
    }
    OrderType(historyOrder) {
        return historyOrder.OrderType;
    }
    OrderTypeStr(historyOrder) {
        return Resources.getResource('property.' + OrderUtils.getOrderTypeLocalizationKey(historyOrder.OrderType));
    }
    ValidityStr(historyOrder) {
        const instrument = historyOrder.Instrument;
        if (instrument === null || instrument === undefined || instrument.InstrType === InstrumentTypes.FORWARD) {
            return '';
        }
        const tif = historyOrder.TimeInForce;
        if (tif === null || tif === void 0 ? void 0 : tif.type) {
            const type = tif.type;
            const expirationTime = tif.expirationTime
                ? ` ${DateTimeUtils.formatDate(tif.expirationTime, 'DD.MM.YYYY  HH:mm:ss')}`
                : '';
            return Resources.getResource('property.' + OrderTifMap[type]) + expirationTime;
        }
        else {
            return '';
        }
    }
    Price(historyOrder) {
        return historyOrder.Price;
    }
    PriceStr(historyOrder) {
        const price = this.Price(historyOrder);
        if (isNaN(price)) {
            return '';
        }
        const instrument = historyOrder.Instrument;
        if (instrument !== null && instrument !== undefined) {
            return instrument.formatPricePrecision(price, false, false);
        }
        else {
            return PriceFormatter.formatPrice(price, 2);
        }
    }
    StopPrice(historyOrder) {
        return historyOrder.StopLimit;
    }
    StopPriceStr(historyOrder) {
        const stopPrice = this.StopPrice(historyOrder);
        if (isNaN(stopPrice)) {
            return '';
        }
        const instrument = historyOrder.Instrument;
        if (instrument !== null && instrument !== undefined) {
            return instrument.formatPricePrecision(stopPrice, false, false);
        }
        else {
            return PriceFormatter.formatPrice(stopPrice, 2);
        }
    }
    ExecutionTypeStr(historyOrder) {
        return this.localizeOrderExecutionType(historyOrder.eventTypeRaw);
    }
    GetBought(historyOrder) {
        return BoughtSoldUtils.Bought(historyOrder);
    }
    GetSold(historyOrder) {
        return BoughtSoldUtils.Sold(historyOrder);
    }
    GetBoughtStr(historyOrder) {
        return BoughtSoldUtils.BoughtStr(historyOrder);
    }
    GetSoldStr(historyOrder) {
        return BoughtSoldUtils.SoldStr(historyOrder);
    }
    Date(historyOrder) {
        return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(historyOrder.Date);
    }
    DateStr(historyOrder) {
        const date = this.Date(historyOrder);
        return DateTimeUtils.formatDate(date, 'DD.MM.YYYY  HH:mm:ss');
    }
    TradingExchangeStr(historyOrder) {
        var _a;
        const instrument = historyOrder.Instrument;
        if (instrument !== null && instrument !== undefined) {
            return instrument.TradingExchange;
        }
        else {
            return (_a = historyOrder.TradingExchange) !== null && _a !== void 0 ? _a : '';
        }
    }
    localizeOrderExecutionType(executionType) {
        if (MathUtils.IsNullOrUndefined(executionType)) {
            return '';
        }
        let key = 'UNKNOWN';
        switch (executionType) {
            case OrderExecutionType.UNKNOWN:
                key = 'UNKNOWN';
                break;
            case OrderExecutionType.PENDING_NEW:
                key = 'PENDING_NEW';
                break;
            case OrderExecutionType.PENDING_EXECUTION:
                key = 'PENDING_EXECUTION';
                break;
            case OrderExecutionType.PENDING_CANCEL:
                key = 'PENDING_CANCEL';
                break;
            case OrderExecutionType.PENDING_REPLACE:
                key = 'PENDING_REPLACE';
                break;
            case OrderExecutionType.PENDING_REPLACE_NOT_ACTIVE:
                key = 'PENDING_REPLACE_NOT_ACTIVE';
                break;
            case OrderExecutionType.NEW:
                key = 'NEW';
                break;
            case OrderExecutionType.ACCEPTED:
                key = 'ACCEPTED';
                break;
            case OrderExecutionType.REPLACED:
                key = 'REPLACED';
                break;
            case OrderExecutionType.PART_FILLED:
                key = 'PART_FILLED';
                break;
            case OrderExecutionType.FILLED:
                key = 'FILLED';
                break;
            case OrderExecutionType.CANCELED:
                key = 'CANCELED';
                break;
            case OrderExecutionType.REFUSED:
                key = 'REFUSED';
                break;
            case OrderExecutionType.RESTATED:
                key = 'RESTATED';
                break;
            case OrderExecutionType.ACTIVATED:
                key = 'ACTIVATED';
                break;
        }
        return Resources.getResource('Order.ExecutionType.' + key);
    }
}
export const HistoryOrderFormatter = new _HistoryOrderFormatter();
